// ============================================================================
// GalleryPlayground — the PRIMARY try-before-install demo (PRD §1.9-B, D5).
// ============================================================================
// The prospect drags the SAME action / body-part / graphicness sliders the
// product ships and watches a curated gallery reclassify LIVE (ignored ↔
// flagged + described). There is NO backend and NO arbitrary upload — it runs
// entirely off the PRECOMPUTED placeholder dataset (demoData.ts) through a
// faithful TS port of the device gating (gating.ts) + describer (describe.ts).
//
// HARD GUARDRAIL: the tiles are abstract SVG/gradient placeholders with TEXT
// labels only. No real explicit imagery exists anywhere in this site. The
// curated legally-sourced/synthetic set is Mike's to plug in (see demoData.ts).
// ============================================================================

import { useMemo, useState } from "react";
import {
  SensitivitySlider,
  uiToPipelineInt,
  type UiStop,
} from "../components/SensitivitySlider";
import { DEMO_TILES } from "./demoData";
import { gateBySensitivity } from "./gating";
import { describeAlert } from "./describe";
import type { DemoTile, GraphicnessLevel } from "./types";

const GRAPHICNESS: { value: GraphicnessLevel; label: string; blurb: string }[] =
  [
    {
      value: "explicit",
      label: "Explicit",
      blurb: "Blunt, anatomically specific wording in the alert.",
    },
    {
      value: "clinical",
      label: "Clinical",
      blurb: "Factual, neutral medical-style wording.",
    },
    {
      value: "tame",
      label: "Tame",
      blurb: "A soft, non-graphic summary — no explicit findings line.",
    },
  ];

export function GalleryPlayground() {
  // Default to the product's recommended "Balanced" on both axes and clinical
  // graphicness (mirrors the customer-app defaults).
  const [action, setAction] = useState<UiStop>(2);
  const [bodypart, setBodypart] = useState<UiStop>(2);
  const [graphic, setGraphic] = useState<GraphicnessLevel>("clinical");

  const actionInt = uiToPipelineInt(action);
  const bodypartInt = uiToPipelineInt(bodypart);

  // Recompute every tile's verdict whenever a slider moves. Pure + cheap.
  const results = useMemo(
    () =>
      DEMO_TILES.map((tile) => {
        const gate = gateBySensitivity({
          classification: {
            parts: tile.parts,
            actions: tile.actions,
            isExplicit: tile.isExplicit,
          },
          gatekeeperScore: tile.score,
          actionSensitivity: actionInt,
          bodypartSensitivity: bodypartInt,
        });
        return { tile, gate };
      }),
    [actionInt, bodypartInt],
  );

  const flaggedCount = results.filter((r) => r.gate.alert).length;

  return (
    <div className="playground">
      <aside className="controls">
        <h3>Detection settings</h3>
        <p className="sub">
          The same controls a parent or sponsor sets per device. Drag them and
          watch the gallery on the right reclassify instantly.
        </p>

        <SensitivitySlider
          title="Action sensitivity"
          subtitle="Which acts trigger an alert"
          value={action}
          onChange={setAction}
        />
        <SensitivitySlider
          title="Body-part sensitivity"
          subtitle="Which exposed body parts trigger an alert"
          value={bodypart}
          onChange={setBodypart}
        />

        <div className="ctrl">
          <div className="ctrl-head">
            <span className="ctrl-title">Description graphicness</span>
          </div>
          <div className="ctrl-sub">How bluntly an alert describes it</div>
          <div className="seg">
            {GRAPHICNESS.map((g) => (
              <button
                key={g.value}
                type="button"
                className={"seg-btn" + (graphic === g.value ? " active" : "")}
                onClick={() => setGraphic(g.value)}
              >
                {g.label}
              </button>
            ))}
          </div>
          <p className="ctrl-legend">
            {GRAPHICNESS.find((g) => g.value === graphic)?.blurb}
          </p>
        </div>

        <div className="summary-bar">
          At these settings,{" "}
          {flaggedCount === 0 ? (
            <b className="safe">nothing in the gallery flags</b>
          ) : (
            <b>
              {flaggedCount} of {results.length} flag
            </b>
          )}
          .
        </div>
      </aside>

      <div className="gallery">
        {results.map(({ tile, gate }) => (
          <GalleryTile
            key={tile.id}
            tile={tile}
            flagged={gate.alert}
            reason={reasonText(gate)}
            graphic={graphic}
          />
        ))}
      </div>
    </div>
  );
}

// A short mono "why" line so the gating is legible to the prospect.
function reasonText(gate: ReturnType<typeof gateBySensitivity>): string {
  if (!gate.alert) return "Below both thresholds — ignored.";
  const bits: string[] = [];
  if (gate.actionAlert) bits.push("action axis");
  if (gate.bodypartAlert) bits.push("body-part axis");
  if (gate.safetyNet) bits.push("high-confidence safety net");
  return "Flagged by: " + bits.join(" + ") + ".";
}

function GalleryTile({
  tile,
  flagged,
  reason,
  graphic,
}: {
  tile: DemoTile;
  flagged: boolean;
  reason: string;
  graphic: GraphicnessLevel;
}) {
  const described = flagged
    ? describeAlert(graphic, tile.scenes, tile.parts, tile.actions)
    : null;

  return (
    <article className={"gtile" + (flagged ? " flagged" : "")}>
      <div className="gtile-thumb" style={{ background: tile.swatch }}>
        <span className="placeholder-note">placeholder</span>
        <span className="band">{tile.band}</span>
      </div>
      <div className="gtile-body">
        <div className="gtile-label">{tile.label}</div>

        <span className={"verdict " + (flagged ? "flag" : "ignore")}>
          <span className="pip" />
          {flagged ? "Flagged" : "Ignored"}
        </span>

        {described && (
          <div className="gtile-alert">
            {described.findingsLine && (
              <div className="findings">{described.findingsLine}</div>
            )}
            <div className="scene">{described.sceneText}</div>
          </div>
        )}

        <div className="gtile-reason">{reason}</div>
      </div>
    </article>
  );
}
